<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, useId } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCircleExclamation, faXmark } from '@fortawesome/pro-solid-svg-icons';

import type { ParsedAddress } from '../types';
import { US_STATES } from '../constants/us-states';
export type { ParsedAddress };

// New Places API types (PlaceAutocompleteElement, March 2025+)
interface AddressComponent {
  types: string[];
  longText: string;
  shortText: string;
}
interface NewPlace {
  addressComponents?: AddressComponent[];
  formattedAddress?: string;
  location?: { lat(): number; lng(): number };
  id?: string;
  fetchFields(opts: { fields: string[] }): Promise<void>;
}
interface PlacePrediction {
  toPlace(): NewPlace;
}

interface Props {
  label: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  modelValue?: ParsedAddress | null;
  initialManualMode?: boolean;
  // Opt-in field styling. 'default' keeps the original look (used by the
  // schedule wizard, create-account, schedule, embedded schedule); 'sketch'
  // renders the Profile sketch-6.2 .pinput/.flabel recipe on the manual fields.
  fieldVariant?: 'default' | 'sketch';
  // Opt-in combined layout (Profile address modal, sketch 6.2 data-modal=address):
  // show the Google search box AND the Street/Unit/City/State/ZIP fields at once
  // (a search pick autofills them), with no mode toggle. Default keeps the
  // search-OR-manual toggle behavior the other consumers rely on.
  combinedFields?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Start typing your address…',
  error: '',
  disabled: false,
  modelValue: null,
  initialManualMode: false,
  fieldVariant: 'default',
  combinedFields: false,
});

const emit = defineEmits<{
  select: [address: ParsedAddress];
  clear: [];
}>();

const inputId = useId();
const errorId = computed(() => `${inputId}-error`);
const hasError = computed(() => props.error.length > 0);

const containerRef = ref<HTMLDivElement | null>(null);
const pacElement = ref<HTMLElement | null>(null);

const gmpReady = ref<boolean>(false);
const manualMode = ref<boolean>(false);
const manualStreet = ref<string>('');
const manualSuite = ref<string>('');
// In combined mode the Unit/Apt field is visible by default (sketch 6.2);
// otherwise it stays behind the "+ Apt / Suite / Floor" trigger.
const showManualSuite = ref<boolean>(props.combinedFields);
const googleSuite = ref<string>('');
const showGoogleSuite = ref<boolean>(false);
const manualCity = ref<string>('');
const manualState = ref<string>('');
const manualZip = ref<string>('');

const streetTouched = ref(false);
const cityTouched = ref(false);
const stateTouched = ref(false);
const zipTouched = ref(false);
const manualZipFormatError = ref<string>('');

// Pre-populate for editing: set fields from modelValue without emitting (parent
// already holds the correct ParsedAddress; emit only fires when the user changes something)
if (props.initialManualMode && props.modelValue) {
  manualMode.value = true;
  manualStreet.value = props.modelValue.streetName;
  if (props.modelValue.suite) {
    manualSuite.value = props.modelValue.suite;
    showManualSuite.value = true;
  }
  manualCity.value = props.modelValue.city;
  manualState.value = props.modelValue.state;
  manualZip.value = props.modelValue.zipCode;
}

// Tailwind class literals — full strings, never interpolated fragments.
// libs/ui/src/**/*.vue is in @source scope (Phase 1 main.css).
// CRITICAL: never interpolate fragments — every class must be a complete name.
const INPUT_BASE =
  'h-10 w-full px-3 py-2 text-base bg-field rounded-md text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-offset-0';
const INPUT_NORMAL = 'border border-border-strong focus:ring-primary';
const INPUT_ERROR = 'border border-danger focus:ring-danger';
const INPUT_DISABLED = 'opacity-50 cursor-not-allowed';

// Sketch-6.2 .pinput recipe (opt-in via fieldVariant='sketch'). Full literals.
const SKETCH_BASE =
  'w-full rounded-md border bg-field px-3 py-2.25 text-sm text-foreground placeholder:text-muted transition-colors focus:outline focus:outline-2 focus:outline-offset-1';
const SKETCH_NORMAL =
  'border-border focus:border-primary focus:outline-primary/45';
const SKETCH_ERROR =
  'border-danger focus:border-primary focus:outline-danger/45';

function buildClass(isError: boolean): string {
  const disabledCls = props.disabled ? ` ${INPUT_DISABLED}` : '';
  if (props.fieldVariant === 'sketch') {
    return `${SKETCH_BASE} ${isError ? SKETCH_ERROR : SKETCH_NORMAL}${disabledCls}`.trim();
  }
  return `${INPUT_BASE} ${isError ? INPUT_ERROR : INPUT_NORMAL}${disabledCls}`.trim();
}

// Field label style — sketch .flabel vs the default label look.
const labelClass = computed(() =>
  props.fieldVariant === 'sketch'
    ? 'mb-1 block text-xs font-medium text-muted'
    : 'text-sm font-normal text-foreground mb-1',
);

// Street/city/state/zip validate themselves on blur; zip also turns red on coverage failure.
const streetInputClass = computed(() =>
  buildClass(streetTouched.value && !manualStreet.value.trim()),
);
const cityInputClass = computed(() =>
  buildClass(cityTouched.value && !manualCity.value.trim()),
);
const stateInputClass = computed(() =>
  buildClass(stateTouched.value && !manualState.value.trim()),
);
const zipInputClass = computed(() =>
  buildClass(
    (zipTouched.value && !manualZip.value.trim()) ||
      hasError.value ||
      manualZipFormatError.value.length > 0,
  ),
);
const inputClass = computed(() => buildClass(hasError.value));
const inputClassOptional = computed(() => buildClass(false));

function parseNewPlace(place: NewPlace): ParsedAddress {
  const get = (type: string, useShort = false): string => {
    const comp = place.addressComponents?.find((c) => c.types.includes(type));
    if (!comp) return '';
    return useShort ? comp.shortText : comp.longText;
  };
  return {
    streetNumber: get('street_number'),
    streetName: get('route'),
    city:
      get('locality') ||
      get('sublocality') ||
      get('administrative_area_level_2'),
    state: get('administrative_area_level_1', true),
    zipCode: get('postal_code'),
    country: get('country', true),
    latitude: place.location?.lat() ?? null,
    longitude: place.location?.lng() ?? null,
    formattedAddress: place.formattedAddress ?? '',
    placeId: place.id ?? '',
  };
}

function loadMapsScript(apiKey: string): Promise<void> {
  // Check for importLibrary specifically — with loading=async the bootstrap sets
  // window.google.maps early, but importLibrary isn't available until the API is
  // fully initialized (callback fires). Guard against the partial-init case.
  if (
    typeof google !== 'undefined' &&
    (google.maps as unknown as Record<string, unknown>)['importLibrary']
  )
    return Promise.resolve();
  return new Promise((resolve, reject) => {
    if (document.querySelector('script[data-maps-loader]')) {
      window.addEventListener('google-maps-ready', () => resolve(), {
        once: true,
      });
      return;
    }
    // Use callback= so Google calls us only after the API is fully ready.
    // script.onload fires too early with loading=async (sub-scripts still loading).
    const cbName = '__googleMapsReady__';
    (window as unknown as Record<string, unknown>)[cbName] = () => {
      window.dispatchEvent(new Event('google-maps-ready'));
      resolve();
    };
    const script = document.createElement('script');
    script.setAttribute('data-maps-loader', '');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&callback=${cbName}`;
    script.async = true;
    script.onerror = () =>
      reject(new Error('[AddressAutocomplete] Google Maps failed to load'));
    document.head.appendChild(script);
  });
}

// Patch attachShadow to open the shadow DOM and inject design-system styles.
// The component uses adoptedStyleSheets with light-dark() values — our <style>
// injection wins because it is appended after adoptedStyleSheets in cascade order.
// All var(--color-*) references inherit from the host via shadow DOM custom-property
// inheritance and update automatically on html.dark toggle.
function patchGmpShadowDOM(): void {
  const w = window as unknown as Record<string, unknown>;
  if (w.__gmpShadowPatched) return;
  const orig = Element.prototype.attachShadow;
  Element.prototype.attachShadow = function (init) {
    if (this.localName === 'gmp-place-autocomplete') {
      const shadow = orig.call(this, { ...init, mode: 'open' });
      const style = document.createElement('style');
      // adoptedStyleSheets are applied after <style> elements in the shadow DOM
      // cascade, so !important is required on every override to win.
      style.textContent = `
        /* Font — override Google Sans with app font */
        :host { font-family: inherit !important; font-size: 1rem !important; }
        input  { font-size: 1rem !important; }

        /* Search icon — hide entirely. The "Service address" label + the
           explicit placeholder already signal the input's purpose; the
           magnifying-glass adds noise inside the field. */
        .input-container div.autocomplete-icon {
          display: none !important;
        }

        /* Clear button — resize from 48px to fit 40px host */
        .input-container button.clear-button {
          height: 28px !important;
          width: 28px !important;
          padding: 4px !important;
          margin-right: 6px !important;
          border-radius: 50% !important;
          transition: background-color 150ms ease, color 150ms ease !important;
        }
        .input-container button.clear-button svg {
          height: 16px !important;
          width: 16px !important;
        }
        .input-container button.clear-button svg path {
          fill: var(--color-muted) !important;
          transition: fill 150ms ease !important;
        }
        .input-container button.clear-button:hover svg path {
          fill: var(--color-foreground) !important;
        }
        .input-container button.clear-button:focus-visible {
          outline: 2px solid var(--color-primary) !important;
          outline-offset: 1px !important;
        }
        .input-container button.clear-button:focus-visible svg path {
          fill: var(--color-foreground) !important;
        }
        .input-container button.clear-button:active svg path {
          fill: var(--color-foreground) !important;
        }

        /* Dropdown panel */
        .dropdown {
          background-color: var(--color-surface) !important;
          border: 1px solid var(--color-border) !important;
          border-radius: 8px !important;
          box-shadow: var(--shadow-lg) !important;
        }
        .dropdown > ul > li {
          border-bottom-color: var(--color-border) !important;
        }
        .dropdown > ul > li:hover {
          background-color: var(--color-canvas) !important;
        }
        .dropdown > ul > li[aria-selected='true'] {
          background-color: var(--color-primary-light) !important;
        }

        /* Suggestion row text */
        .place-autocomplete-element-place-name {
          color: var(--color-foreground) !important;
          font-family: inherit !important;
          font-size: 0.875rem !important;
          font-weight: 500 !important;
          line-height: 1.25rem !important;
        }
        .place-autocomplete-element-text-div {
          color: var(--color-muted) !important;
          font-family: inherit !important;
        }
        .place-autocomplete-element-place-details {
          font-family: inherit !important;
          font-size: 0.75rem !important;
          line-height: 1rem !important;
        }

        /* Location pin icon in suggestion rows */
        .place-autocomplete-element-prediction-item-icon {
          background-color: var(--color-border) !important;
        }
        .place-autocomplete-element-prediction-item-icon path {
          fill: var(--color-muted) !important;
        }

        /* PODZ-1611: PAC opens a full-window <dialog> on phone-width with
           position: fixed; top: 0. On iOS, position:fixed anchors to the
           LAYOUT viewport — when the keyboard opens, iOS auto-scrolls
           focused inputs toward the top of the visible visual viewport,
           but the layout viewport stays put. visualViewport.offsetTop
           becomes positive, and the dialog at top:0 lands ABOVE the visible
           viewport (offscreen). Fix: mirror visualViewport.offsetTop into
           --pac-vv-offset on the host (JS, see onMounted below) and read
           it here. Falls back to 0 on non-iOS / desktop. */
        dialog[open].full-window-autocomplete-dialog {
          top: var(--pac-vv-offset, 0px) !important;
          height: calc(100% - var(--pac-vv-offset, 0px)) !important;
        }

        /* Match BaseInput padding (px-3 py-2) on the inner input.
           Google ships the input with no horizontal padding and a 48px input-container;
           our outer ring is h-10, so we need padding on the input itself. */
        .input-container input {
          padding-left: 0.75rem !important;
          padding-right: 0.75rem !important;
        }

        /* Suppress browser default focus outline and the input-container's blue
           focus ring (Google ships an outline on focus-within that paints blue on
           top of our orange ring). Our outer container handles focus styling via
           focus-within (see template change in Task 2). */
        .input-container,
        .input-container:focus-within,
        .input-container input,
        .input-container input:focus,
        .input-container input:focus-visible {
          outline: none !important;
          box-shadow: none !important;
        }

        /* Hide Google's internal focus-ring overlay element. PAC renders a
           dedicated <div class="focus-ring" part="focus-ring"> that paints
           the blue ring on top of our container — neither outline nor
           box-shadow rules above suppress it because it is a separate node. */
        .focus-ring,
        [part='focus-ring'] {
          display: none !important;
        }
      `;
      shadow.appendChild(style);
      return shadow;
    }
    return orig.call(this, init);
  };
  w.__gmpShadowPatched = true;
}

// PODZ-1611: mirror window.visualViewport.offsetTop into a CSS custom
// property on the PAC host. CSS variables inherit through shadow DOM
// boundaries, so the shadow's dialog[open] rule resolves the value
// correctly. Updated on focus + on visualViewport resize/scroll events.
function syncVvOffset(pac: HTMLElement | null): void {
  if (!pac) return;
  const offset = window.visualViewport?.offsetTop ?? 0;
  pac.style.setProperty('--pac-vv-offset', `${offset}px`);
}

// Setup-scope listener so onUnmounted can register cleanly (Vue 3 lifecycle
// hooks must be called synchronously during setup). The listener is added
// from onMounted once the PAC element exists; the flag gates removal.
let vvListenerAttached = false;
const onVvListener = (): void => syncVvOffset(pacElement.value);
onUnmounted(() => {
  if (!vvListenerAttached) return;
  window.visualViewport?.removeEventListener('resize', onVvListener);
  window.visualViewport?.removeEventListener('scroll', onVvListener);
});

onMounted(async () => {
  const apiKey = import.meta.env.VITE_GOOGLE_KEY;
  if (!apiKey) {
    console.warn(
      '[AddressAutocomplete] VITE_GOOGLE_KEY is empty — Places autocomplete is disabled. Add the key to .env.local for dev.',
    );
    gmpReady.value = true;
    return;
  }
  patchGmpShadowDOM();
  await loadMapsScript(apiKey);

  const { PlaceAutocompleteElement } = (await google.maps.importLibrary(
    'places',
  )) as unknown as {
    PlaceAutocompleteElement: new (opts?: object) => HTMLElement & {
      placeholder: string;
      addEventListener(
        event: 'gmp-select',
        handler: (e: { placePrediction: PlacePrediction }) => void,
      ): void;
    };
  };

  const pac = new PlaceAutocompleteElement({ types: ['address'] });
  pacElement.value = pac as unknown as HTMLElement;

  // Set the placeholder via the element's own first-class `placeholder` property
  // (documented Places API). Injecting into the inner shadow <input> via
  // setAttribute does NOT stick — Google re-renders/manages that input and
  // clears any externally-set attribute, so the placeholder vanished.
  pac.placeholder = props.placeholder;
  // PODZ-1611: prime --pac-vv-offset immediately, then keep it in sync with
  // iOS visual-viewport changes (keyboard open/close, page scroll while
  // keyboard up). The CSS rule injected by patchGmpShadowDOM reads this
  // custom property through the shadow boundary. Listener add/remove is
  // managed at setup scope below (lifecycle hooks can't be registered
  // inside an async onMounted after awaits).
  syncVvOffset(pacElement.value);
  vvListenerAttached = true;
  window.visualViewport?.addEventListener('resize', onVvListener);
  window.visualViewport?.addEventListener('scroll', onVvListener);
  // Append is handled by the containerRef watcher below — setting gmpReady
  // causes the v-if to mount containerRef, which triggers the watcher.

  pac.addEventListener('gmp-select', async ({ placePrediction }) => {
    const place = placePrediction.toPlace();
    await place.fetchFields({
      fields: ['addressComponents', 'formattedAddress', 'location', 'id'],
    });
    const parsedAddr = parseNewPlace(place);
    // Combined mode: autofill the always-visible manual fields from the pick and
    // route the emit through submitManualAddress so those fields are the single
    // source of truth (no drift if the user edits after picking). placeId/lat/lng
    // are dropped — the modal does not consume them.
    if (props.combinedFields) {
      manualStreet.value = [parsedAddr.streetNumber, parsedAddr.streetName]
        .filter(Boolean)
        .join(' ');
      manualCity.value = parsedAddr.city;
      manualState.value = parsedAddr.state;
      manualZip.value = parsedAddr.zipCode;
      submitManualAddress();
      return;
    }
    emit('select', {
      ...parsedAddr,
      suite: googleSuite.value.trim() || undefined,
    });
  });

  // Emit 'clear' when the X button inside the PAC clears the field.
  // Click events from shadow DOM are composed (bubble to host). Defer one tick
  // so Google's handler runs first and updates pac.value before we check it.
  pac.addEventListener('click', () => {
    setTimeout(() => {
      if (!(pac as unknown as { value: string }).value) emit('clear');
    }, 0);
  });

  // Pre-fill the autocomplete input with the existing address when editing
  if (!manualMode.value && props.modelValue?.formattedAddress) {
    (pac as unknown as { value: string }).value =
      props.modelValue.formattedAddress;
  }

  gmpReady.value = true;
});

// When the user toggles back from manual mode, containerRef is recreated by v-if.
// Re-append the stored PAC element so it reappears.
watch(containerRef, (container) => {
  if (container && pacElement.value && !container.contains(pacElement.value)) {
    container.appendChild(pacElement.value);
  }
});

function onManualStreetInput(e: Event): void {
  manualStreet.value = (e.target as HTMLInputElement).value;
  submitManualAddress();
}

function onManualSuiteInput(e: Event): void {
  manualSuite.value = (e.target as HTMLInputElement).value;
  submitManualAddress();
}

function onManualCityInput(e: Event): void {
  manualCity.value = (e.target as HTMLInputElement).value;
  submitManualAddress();
}

function onManualStateInput(e: Event): void {
  manualState.value = (e.target as HTMLInputElement).value;
  submitManualAddress();
}

function onManualZipInput(e: Event): void {
  manualZip.value = (e.target as HTMLInputElement).value;
  manualZipFormatError.value = '';
  submitManualAddress();
}

function submitManualAddress(): void {
  const street = manualStreet.value.trim();
  const suite = manualSuite.value.trim();
  const city = manualCity.value.trim();
  const state = manualState.value.trim();
  const zip = manualZip.value.trim();
  if (!street && !city && !state) return;
  const streetName = street + (suite ? ', ' + suite : '');
  const formattedAddress = [
    street + (suite ? ' ' + suite : ''),
    city,
    state,
    zip,
  ]
    .filter(Boolean)
    .join(', ');
  emit('select', {
    streetNumber: '',
    streetName,
    suite: suite || undefined,
    city,
    state,
    zipCode: zip,
    country: '',
    latitude: null,
    longitude: null,
    formattedAddress,
    placeId: '',
  });
}

function onManualStreetBlur(): void {
  streetTouched.value = true;
}
function onManualCityBlur(): void {
  cityTouched.value = true;
}
function onManualStateBlur(): void {
  stateTouched.value = true;
}
function onManualZipBlur(): void {
  zipTouched.value = true;
  const zip = manualZip.value.trim();
  if (zip && !/^\d{5}$/.test(zip)) {
    manualZipFormatError.value = 'Please enter a valid 5-digit zip code.';
  } else {
    manualZipFormatError.value = '';
  }
}

function toggleMode(): void {
  // Switching modes always starts fresh: clear every field in both modes and
  // the Google input, then signal the parent to drop the selected address.
  // Stale values from one mode must never bleed into the other.
  manualStreet.value = '';
  manualSuite.value = '';
  manualCity.value = '';
  manualState.value = '';
  manualZip.value = '';
  showManualSuite.value = false;
  manualZipFormatError.value = '';
  streetTouched.value = false;
  cityTouched.value = false;
  stateTouched.value = false;
  zipTouched.value = false;
  googleSuite.value = '';
  showGoogleSuite.value = false;
  if (pacElement.value) {
    (pacElement.value as unknown as { value: string }).value = '';
  }
  emit('clear');
  manualMode.value = !manualMode.value;
}

// When the parent signals an error (submit attempt with incomplete address),
// force-touch all required manual fields so empty ones go red immediately.
watch(
  () => props.error,
  (val) => {
    if (val && manualMode.value) {
      streetTouched.value = true;
      cityTouched.value = true;
      stateTouched.value = true;
      zipTouched.value = true;
      const zip = manualZip.value.trim();
      if (zip && !/^\d{5}$/.test(zip)) {
        manualZipFormatError.value = 'Please enter a valid 5-digit zip code.';
      }
    }
  },
);
</script>

<template>
  <div
    class="flex flex-col"
    data-testid="address-autocomplete"
    :data-field-variant="fieldVariant"
  >
    <template v-if="!manualMode || combinedFields">
      <label :for="inputId" :class="[labelClass, { 'text-muted': disabled }]">
        {{ label }}
      </label>
      <!-- PlaceAutocompleteElement is appended here by onMounted (and re-appended
           on toggle-back via the containerRef watcher in script). -->
      <div
        v-if="!gmpReady"
        class="h-10 w-full rounded-md border border-border-strong bg-canvas animate-pulse"
        aria-hidden="true"
      />
      <div
        v-else
        ref="containerRef"
        :id="inputId"
        :aria-invalid="hasError ? 'true' : undefined"
        :aria-describedby="hasError ? errorId : undefined"
        data-testid="address-autocomplete-input"
        class="w-full rounded-md focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-0"
        :class="
          hasError ? 'focus-within:ring-danger' : 'focus-within:ring-primary'
        "
      />
      <span
        v-if="hasError"
        :id="errorId"
        class="text-sm text-danger mt-1 flex items-center"
        data-testid="address-autocomplete-error"
      >
        <FontAwesomeIcon :icon="faCircleExclamation" class="mr-1 size-4" />
        {{ error }}
      </span>
      <!-- Google attribution hint (sketch 6.2 .ac-hint), combined mode only. -->
      <p
        v-if="combinedFields"
        class="mt-1 text-right text-2xs text-subtle"
        data-testid="address-search-hint"
      >
        Search powered by Google
      </p>
      <slot v-if="!combinedFields" name="below-input" />
      <button
        v-if="!combinedFields"
        type="button"
        class="text-sm text-primary mt-2 self-start hover:underline focus:outline-none"
        data-testid="address-manual-toggle"
        @click="toggleMode"
      >
        Enter address manually
      </button>
      <!-- Suite: google mode only. Combined mode uses the always-visible manual
           Unit field instead, so neither google-suite template renders. -->
      <template v-if="!combinedFields && !showGoogleSuite">
        <button
          type="button"
          class="text-xs text-muted mt-1.5 self-start hover:text-foreground hover:underline focus:outline-none"
          data-testid="address-google-suite-trigger"
          @click="showGoogleSuite = true"
        >
          + Apt / Suite / Floor
        </button>
      </template>
      <template v-else-if="!combinedFields && showGoogleSuite">
        <label :class="[labelClass, 'mt-3']">Apt / Suite / Floor</label>
        <div class="flex items-center gap-1.5">
          <input
            :value="googleSuite"
            :disabled="disabled"
            :class="inputClassOptional"
            type="text"
            maxlength="50"
            autocomplete="off"
            placeholder="e.g. Apt 2B"
            data-testid="address-google-suite"
            @input="googleSuite = ($event.target as HTMLInputElement).value"
          />
          <button
            type="button"
            class="shrink-0 size-8 flex items-center justify-center rounded-md border border-border text-muted hover:text-danger hover:border-danger focus:outline-none"
            aria-label="Remove suite field"
            @click="
              showGoogleSuite = false;
              googleSuite = '';
            "
          >
            <FontAwesomeIcon :icon="faXmark" class="text-[13px]" />
          </button>
        </div>
      </template>
    </template>
    <template v-if="manualMode || combinedFields">
      <label :class="[labelClass, { 'mt-3': combinedFields }]">{{
        combinedFields ? 'Street' : 'Street Address'
      }}</label>
      <input
        :value="manualStreet"
        :disabled="disabled"
        :class="streetInputClass"
        type="text"
        maxlength="100"
        autocomplete="off"
        data-testid="address-manual-street"
        @input="onManualStreetInput"
        @blur="onManualStreetBlur"
      />
      <span
        v-if="streetTouched && !manualStreet.trim()"
        class="text-sm text-danger mt-1 flex items-center"
      >
        <FontAwesomeIcon :icon="faCircleExclamation" class="mr-1 size-4" />
        Street address is required.
      </span>
      <!-- Unit/Apt + City: 2-col row in combined mode (sketch frow2); stacked
           otherwise via display:contents (no layout change for other consumers). -->
      <div
        :class="combinedFields ? 'mt-3 grid grid-cols-2 gap-2.5' : 'contents'"
      >
        <!-- Unit / Apt -->
        <div :class="combinedFields ? '' : 'contents'">
          <!-- Suite: hidden behind a trigger in default mode; always shown in
               combined mode (showManualSuite is initialized from the prop). -->
          <template v-if="!showManualSuite">
            <button
              type="button"
              class="text-xs text-muted mt-1.5 self-start hover:text-foreground hover:underline focus:outline-none"
              data-testid="address-manual-suite-trigger"
              @click="showManualSuite = true"
            >
              + Apt / Suite / Floor
            </button>
          </template>
          <template v-else>
            <label :class="[labelClass, { 'mt-3': !combinedFields }]">{{
              combinedFields ? 'Unit / Apt (optional)' : 'Apt / Suite / Floor'
            }}</label>
            <div class="flex items-center gap-1.5">
              <input
                :value="manualSuite"
                :disabled="disabled"
                :class="inputClassOptional"
                type="text"
                maxlength="50"
                autocomplete="off"
                placeholder="e.g. Apt 2B"
                data-testid="address-manual-suite"
                @input="onManualSuiteInput"
              />
              <button
                v-if="!combinedFields"
                type="button"
                class="shrink-0 size-8 flex items-center justify-center rounded-md border border-border text-muted hover:text-danger hover:border-danger focus:outline-none"
                aria-label="Remove suite field"
                @click="
                  showManualSuite = false;
                  manualSuite = '';
                "
              >
                <FontAwesomeIcon :icon="faXmark" class="text-[13px]" />
              </button>
            </div>
          </template>
        </div>
        <!-- City -->
        <div :class="combinedFields ? '' : 'contents'">
          <label :class="[labelClass, { 'mt-3': !combinedFields }]">City</label>
          <input
            :value="manualCity"
            :disabled="disabled"
            :class="cityInputClass"
            type="text"
            maxlength="100"
            autocomplete="off"
            data-testid="address-manual-city"
            @input="onManualCityInput"
            @blur="onManualCityBlur"
          />
          <span
            v-if="cityTouched && !manualCity.trim()"
            class="text-sm text-danger mt-1 flex items-center"
          >
            <FontAwesomeIcon :icon="faCircleExclamation" class="mr-1 size-4" />
            City is required.
          </span>
        </div>
      </div>
      <!-- State + ZIP: 2-col row in combined mode (sketch frow2); stacked
           otherwise via display:contents. -->
      <div
        :class="combinedFields ? 'mt-3 grid grid-cols-2 gap-2.5' : 'contents'"
      >
        <!-- State -->
        <div :class="combinedFields ? '' : 'contents'">
          <label :class="[labelClass, { 'mt-3': !combinedFields }]"
            >State</label
          >
          <div class="relative">
            <select
              :value="manualState"
              :disabled="disabled"
              :class="
                stateInputClass +
                ' pr-8 appearance-none cursor-pointer bg-field! ' +
                (manualState ? 'text-foreground!' : 'text-muted!')
              "
              autocomplete="address-level1"
              data-testid="address-manual-state"
              @change="onManualStateInput"
              @blur="onManualStateBlur"
            >
              <option value="" disabled :selected="!manualState">
                Select state…
              </option>
              <option v-for="st in US_STATES" :key="st.value" :value="st.value">
                {{ st.label }}
              </option>
            </select>
            <span
              class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted"
              aria-hidden="true"
              >▾</span
            >
          </div>
          <span
            v-if="stateTouched && !manualState.trim()"
            class="text-sm text-danger mt-1 flex items-center"
          >
            <FontAwesomeIcon :icon="faCircleExclamation" class="mr-1 size-4" />
            State is required.
          </span>
        </div>
        <!-- ZIP -->
        <div :class="combinedFields ? '' : 'contents'">
          <label :class="[labelClass, { 'mt-3': !combinedFields }]">{{
            combinedFields ? 'ZIP' : 'Zip Code'
          }}</label>
          <input
            :value="manualZip"
            :disabled="disabled"
            :class="zipInputClass"
            type="text"
            maxlength="5"
            inputmode="numeric"
            autocomplete="off"
            data-testid="address-manual-zip"
            @input="onManualZipInput"
            @blur="onManualZipBlur"
          />
          <span
            v-if="zipTouched && !manualZip.trim()"
            class="text-sm text-danger mt-1 flex items-center"
          >
            <FontAwesomeIcon :icon="faCircleExclamation" class="mr-1 size-4" />
            Zip code is required.
          </span>
          <span
            v-if="manualZipFormatError"
            class="text-sm text-danger mt-1 flex items-center"
            data-testid="address-manual-zip-format-error"
          >
            <FontAwesomeIcon :icon="faCircleExclamation" class="mr-1 size-4" />
            {{ manualZipFormatError }}
          </span>
          <span
            v-if="hasError && manualZip.trim()"
            class="text-sm text-danger mt-1 flex items-center"
            data-testid="address-autocomplete-error"
          >
            <FontAwesomeIcon :icon="faCircleExclamation" class="mr-1 size-4" />
            {{ error }}
          </span>
        </div>
      </div>
      <slot name="below-input" />
      <button
        v-if="!combinedFields"
        type="button"
        class="text-sm text-primary mt-2 self-start hover:underline focus:outline-none"
        data-testid="address-manual-toggle"
        @click="toggleMode"
      >
        Find address using Google Autocomplete
      </button>
    </template>
  </div>
</template>
